import { Animal, AnimalBase } from "../types/interfaces";

/**
 * Helper functions for managing historical arrays (prenies_historial, pesajes, and ubicaciones_historial)
 * These functions mirror Supabase trigger logic but work client-side during sync
 */

export interface PregnancyRecord {
  /** Estado de preñez del registro; null = no se cargó preñez (solo ternero) */
  prenies: string | null;
  fecha_evento: string;
  tiene_ternero?: boolean;
  fecha_creacion: string;
}

export interface WeightRecord {
  peso: number;
  fecha_evento: string;
  fecha_creacion: string;
}

export interface UbicacionRecord {
  ubicacion_id: string;
  fecha_evento: string;
  fecha_creacion: string;
}

/**
 * Updates the prenies_historial array with a new pregnancy record
 * Follows Supabase trigger pattern: append if new, maintain ordering
 */
export function updatePreniesHistorial(
  existingAnimal: Animal | undefined,
  newRecord: AnimalBase
): PregnancyRecord[] | undefined {
  // Only process if there's pregnancy data (must be explicitly set, not just truthy)
  // Match backend trigger: "IF NEW.prenies IS NOT NULL OR NEW.tiene_ternero IS NOT NULL"
  // Treat undefined, null, and empty string as "not set"
  const preniesIsSet = newRecord.prenies !== undefined &&
                      newRecord.prenies !== null &&
                      newRecord.prenies !== '';
  const tieneTerneroIsSet = newRecord.tiene_ternero !== undefined &&
                            newRecord.tiene_ternero !== null;  // Match backend IS NOT NULL

  if (!preniesIsSet && !tieneTerneroIsSet) {
    return existingAnimal?.prenies_historial;
  }

  const existingHistorial = existingAnimal?.prenies_historial || [];
  
  // Create new pregnancy record — el prenies queda con lo que vino:
  // null cuando solo se seteó ternero (KAN-185, NO materializar VACIA)
  const pregnancyRecord: PregnancyRecord = {
    prenies: newRecord.prenies ?? null,
    fecha_evento: newRecord.fecha_de_evento,
    tiene_ternero: newRecord.tiene_ternero,
    fecha_creacion: newRecord.fecha_de_creacion!
  };

  // Check for duplicates based on fecha_evento and prenies value
  const isDuplicate = existingHistorial.some(
    record => 
      record.fecha_evento === pregnancyRecord.fecha_evento &&
      record.prenies === pregnancyRecord.prenies &&
      record.tiene_ternero === pregnancyRecord.tiene_ternero
  );

  if (isDuplicate) {
    return existingHistorial;
  }

  // Append new record and maintain chronological order
  const updatedHistorial = [...existingHistorial, pregnancyRecord];

  // Sort by fecha_evento (oldest first)
  updatedHistorial.sort((a, b) =>
    new Date(a.fecha_evento).getTime() - new Date(b.fecha_evento).getTime()
  );

  // Limit to 50 records (keep the most recent)
  const MAX_PREGNANCY_RECORDS = 50;
  if (updatedHistorial.length > MAX_PREGNANCY_RECORDS) {
    return updatedHistorial.slice(-MAX_PREGNANCY_RECORDS);
  }

  return updatedHistorial;
}

/**
 * Updates the pesajes array with a new weight record
 * Follows Supabase trigger pattern: append if new, maintain ordering
 */
export function updatePesajes(
  existingAnimal: Animal | undefined,
  newRecord: AnimalBase
): WeightRecord[] | undefined {
  // Only process if there's weight data
  if (!newRecord.peso || newRecord.peso === 0) {
    return existingAnimal?.pesajes;
  }

  const existingPesajes = existingAnimal?.pesajes || [];
  
  // Create new weight record
  const weightRecord: WeightRecord = {
    peso: newRecord.peso,
    fecha_evento: newRecord.fecha_de_evento,
    fecha_creacion: newRecord.fecha_de_creacion!
  };

  // Check for duplicates based on fecha_evento and peso value
  const isDuplicate = existingPesajes.some(
    record => 
      record.fecha_evento === weightRecord.fecha_evento &&
      record.peso === weightRecord.peso
  );

  if (isDuplicate) {
    return existingPesajes;
  }

  // Append new record and maintain chronological order
  const updatedPesajes = [...existingPesajes, weightRecord];

  // Sort by fecha_evento (oldest first)
  updatedPesajes.sort((a, b) =>
    new Date(a.fecha_evento).getTime() - new Date(b.fecha_evento).getTime()
  );

  // Limit to 100 records (keep the most recent)
  const MAX_WEIGHT_RECORDS = 100;
  if (updatedPesajes.length > MAX_WEIGHT_RECORDS) {
    return updatedPesajes.slice(-MAX_WEIGHT_RECORDS);
  }

  return updatedPesajes;
}

/**
 * Updates the ubicaciones_historial array with a new location record
 * Follows Supabase trigger pattern: append if new, maintain ordering
 */
export function updateUbicacionesHistorial(
  existingAnimal: Animal | undefined,
  newRecord: AnimalBase
): UbicacionRecord[] | undefined {
  // Only process if there's ubicacion data (must be explicitly set, not just truthy)
  // Match backend trigger: "IF NEW.ubicacion_id IS NOT NULL"
  // Treat undefined, null, and empty string as "not set"
  const ubicacionIdIsSet = newRecord.ubicacion_id !== undefined &&
                          newRecord.ubicacion_id !== null &&
                          newRecord.ubicacion_id !== '';

  if (!ubicacionIdIsSet) {
    return existingAnimal?.ubicaciones_historial;
  }

  const existingUbicaciones = existingAnimal?.ubicaciones_historial || [];

  // Create new ubicacion record
  const ubicacionRecord: UbicacionRecord = {
    ubicacion_id: newRecord.ubicacion_id!,
    fecha_evento: newRecord.fecha_de_evento,
    fecha_creacion: newRecord.fecha_de_creacion!
  };

  // Check for duplicates based on fecha_evento and ubicacion_id
  const isDuplicate = existingUbicaciones.some(
    record =>
      record.fecha_evento === ubicacionRecord.fecha_evento &&
      record.ubicacion_id === ubicacionRecord.ubicacion_id
  );

  if (isDuplicate) {
    return existingUbicaciones;
  }

  // Append new record and maintain chronological order
  const updatedUbicaciones = [...existingUbicaciones, ubicacionRecord];

  // Sort by fecha_evento (oldest first)
  updatedUbicaciones.sort((a, b) =>
    new Date(a.fecha_evento).getTime() - new Date(b.fecha_evento).getTime()
  );

  // Limit to 50 records (keep the most recent)
  const MAX_UBICACION_RECORDS = 50;
  if (updatedUbicaciones.length > MAX_UBICACION_RECORDS) {
    return updatedUbicaciones.slice(-MAX_UBICACION_RECORDS);
  }

  return updatedUbicaciones;
}

/**
 * Validates that historical arrays are properly formatted
 */
export function validateHistoricalArrays(animal: Animal): boolean {
  // Validate prenies_historial — un registro es válido si tiene preñez o
  // ternero seteado (regla de inclusión del trigger) más sus fechas
  if (animal.prenies_historial) {
    for (const record of animal.prenies_historial) {
      const hasPregnancyData = (record.prenies !== null && record.prenies !== undefined && record.prenies !== '')
        || record.tiene_ternero !== undefined;
      if (!hasPregnancyData || !record.fecha_evento || !record.fecha_creacion) {
        console.warn('Invalid pregnancy record:', record);
        return false;
      }
    }
  }

  // Validate pesajes
  if (animal.pesajes) {
    for (const record of animal.pesajes) {
      if (!record.peso || !record.fecha_evento || !record.fecha_creacion) {
        console.warn('Invalid weight record:', record);
        return false;
      }
    }
  }

  // Validate ubicaciones_historial
  if (animal.ubicaciones_historial) {
    for (const record of animal.ubicaciones_historial) {
      if (!record.ubicacion_id || !record.fecha_evento || !record.fecha_creacion) {
        console.warn('Invalid ubicacion record:', record);
        return false;
      }
    }
  }

  return true;
}

/**
 * Get latest pregnancy status from historial
 */
export function getLatestPregnancyStatus(historial: PregnancyRecord[] | undefined): PregnancyRecord | null {
  if (!historial || historial.length === 0) {
    return null;
  }

  // Return the last record (most recent by fecha_evento)
  return historial[historial.length - 1];
}

/**
 * Get latest weight from pesajes
 */
export function getLatestWeight(pesajes: WeightRecord[] | undefined): WeightRecord | null {
  if (!pesajes || pesajes.length === 0) {
    return null;
  }

  // Return the last record (most recent by fecha_evento)
  return pesajes[pesajes.length - 1];
}

/**
 * Get latest ubicacion from ubicaciones_historial
 */
export function getLatestUbicacion(ubicaciones: UbicacionRecord[] | undefined): UbicacionRecord | null {
  if (!ubicaciones || ubicaciones.length === 0) {
    return null;
  }

  // Return the last record (most recent by fecha_evento)
  return ubicaciones[ubicaciones.length - 1];
}

/**
 * Merge historical arrays from multiple records (for bulk operations)
 */
export function mergeHistoricalArrays(
  baseAnimal: Animal,
  newRecords: AnimalBase[]
): { prenies_historial?: PregnancyRecord[]; pesajes?: WeightRecord[]; ubicaciones_historial?: UbicacionRecord[] } {
  let currentAnimal: Animal | undefined = { ...baseAnimal };
  const result: { prenies_historial?: PregnancyRecord[]; pesajes?: WeightRecord[]; ubicaciones_historial?: UbicacionRecord[] } = {};

  for (const record of newRecords) {
    // Update pregnancy historial
    const updatedPrenies = updatePreniesHistorial(currentAnimal, record);
    if (updatedPrenies) {
      result.prenies_historial = updatedPrenies;
      currentAnimal = { ...currentAnimal, prenies_historial: updatedPrenies };
    }

    // Update weight historial
    const updatedPesajes = updatePesajes(currentAnimal, record);
    if (updatedPesajes) {
      result.pesajes = updatedPesajes;
      currentAnimal = { ...currentAnimal, pesajes: updatedPesajes };
    }

    // Update ubicacion historial
    const updatedUbicaciones = updateUbicacionesHistorial(currentAnimal, record);
    if (updatedUbicaciones) {
      result.ubicaciones_historial = updatedUbicaciones;
      currentAnimal = { ...currentAnimal, ubicaciones_historial: updatedUbicaciones };
    }
  }

  return result;
}
